import { useFlags } from "launchdarkly-react-client-sdk";
import { type ReactNode, useMemo } from "react";

import { ChecklistRtl } from "@mui/icons-material";
import AnnouncementIcon from "@mui/icons-material/Announcement";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import CallIcon from "@mui/icons-material/Call";
import ChecklistIcon from "@mui/icons-material/Checklist";
import CloudCircleIcon from "@mui/icons-material/CloudCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ConnectWithoutContactIcon from "@mui/icons-material/ConnectWithoutContact";
import EmailIcon from "@mui/icons-material/Email";
import FaxIcon from "@mui/icons-material/Fax";
import FeedIcon from "@mui/icons-material/Feed";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import HearingIcon from "@mui/icons-material/Hearing";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ListAltIcon from "@mui/icons-material/ListAlt";
import MarkunreadMailboxIcon from "@mui/icons-material/MarkunreadMailbox";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import StarIcon from "@mui/icons-material/Star";
import SvgIcon from "@mui/material/SvgIcon";

import { useAppSelector } from "app/hooks";
import { PodiumIcon, QBSyncIcon } from "components/icons";
import {
  AppointmentsIcon,
  AudiologyIcon,
  BillingIcon,
  EarIcon,
  InsurancePolicyIcon,
  MessagesIcon,
  NoteIcon,
  ProfileIcon
} from "components/icons/filled";
import { type MenuItem } from "components/section-side-menu/section-side-menu.component";
import {
  AI_SUMMARY_FEATURE,
  AUDIOLOGY_REPORTS_FEATURE,
  HEARING_AID_BENEFIT_CALCULATOR,
  MARKETING_AUTOMATION_FEATURE,
  MEDICAL_SCRIBE_FEATURE,
  PATIENT_DOCUMENTS_FEATURE,
  PATIENT_ONBOARDING_FORMS_FEATURE_FLAG,
  PATIENT_PORTAL_SHARING_V2,
  PODIUM_INTEGRATION,
  SCRIBE_FOLLOW_UPS_FEATURE,
  SCRIBE_INSIGHTS_FEATURE,
  VISIT_TRACKER_FEATURE
} from "constants/feature-flags";
import { NOAH_ES } from "constants/patients";
import {
  selectCurrentClinic,
  selectCurrentPractice,
  selectCurrentUser
} from "features/auth/auth-slice";
import { useDirectMailEnabled } from "features/direct-mail/use-direct-mail-enabled.hook";
import useCanUseVoiceScreens from "hooks/use-can-use-voice-screens.hook";
import useUserRoles from "hooks/use-user-roles.hook";
import {
  hasAccessRestrictions,
  hasRequiredAccess
} from "utils/auth-access.utils";

type UsePatientSectionTabsOptions = {
  patientOpenTasksCount?: number;
  scribeInsightsCount?: number;
  audiogramCompareChip?: ReactNode;
  activeVisitAppointmentId?: number;
  /** The new document notice's "3 new" pill, drawn under E-Documents. */
  eDocumentsAnnotation?: ReactNode;
};

export default function usePatientSectionTabs({
  patientOpenTasksCount = 0,
  scribeInsightsCount,
  audiogramCompareChip,
  activeVisitAppointmentId,
  eDocumentsAnnotation
}: UsePatientSectionTabsOptions = {}): MenuItem[] {
  const flags = useFlags();
  const {
    isSunoAdminOrBusinessOwner,
    isAdminOrBusinessOwner,
    isAudiologist,
    isScheduler,
    canAccessQBO,
    userRoles,
    userPermissions
  } = useUserRoles();
  const currentClinic = useAppSelector(selectCurrentClinic);
  const currentPractice = useAppSelector(selectCurrentPractice);
  const currentUser = useAppSelector(selectCurrentUser);

  const isAiSummarizeEnabled = flags[AI_SUMMARY_FEATURE];
  const isPodiumIntegrationEnabled = flags[PODIUM_INTEGRATION] ?? false;
  const isMedicalScribeEnabled =
    Boolean(flags[MEDICAL_SCRIBE_FEATURE]) &&
    (isSunoAdminOrBusinessOwner || isAudiologist);
  const isScribeInsightsEnabled =
    Boolean(flags[SCRIBE_INSIGHTS_FEATURE]) && isMedicalScribeEnabled;
  // Layered on the Scribes gate for the same reason Scribe Insights is: this
  // reads the same recordings, so it needs the same role and the same flag.
  const isFollowUpsEnabled =
    Boolean(flags[SCRIBE_FOLLOW_UPS_FEATURE]) && isMedicalScribeEnabled;
  const patientPortalEnabled =
    Boolean(flags[PATIENT_PORTAL_SHARING_V2]) &&
    Boolean(
      currentPractice?.patient_portal_documents ||
        currentPractice?.patient_portal_reports ||
        currentPractice?.patient_portal_videos
    );
  const hasNoah = currentClinic?.noah_provider === NOAH_ES;
  const quickbooksIsAvailable = Boolean(
    currentPractice?.is_quickbooks_enabled && canAccessQBO
  );
  const isGoogleReviewsEnabled =
    Boolean(currentPractice?.google_reviews_enabled) &&
    Boolean(currentUser?.has_access_to_google_reviews);
  const canUseVoiceScreens = useCanUseVoiceScreens();
  const isRealEarMeasurementEnabled = Boolean(
    currentPractice?.real_ear_measurement_enabled
  );
  // Calls lives inside Touchpoints (with Messages/Emails/Faxes); it only
  // falls back to a top-level tab when the Touchpoints section itself is
  // hidden by its flag.
  const isTouchpointsVisible = Boolean(flags[MARKETING_AUTOMATION_FEATURE]);
  const isDirectMailEnabled = useDirectMailEnabled();

  const menuItems = useMemo<MenuItem[]>(() => {
    const allMenuItems: MenuItem[] = [
      ...(isAiSummarizeEnabled
        ? [
            {
              to: "ai-summary",
              primary: "AI Summary",
              Icon: <NewspaperIcon />,
              isFallbackPath: false,
              tooltip: "AI Summary"
            }
          ]
        : []),
      {
        to: "chart-notes",
        primary: "Chart Notes",
        Icon: <NoteIcon />,
        isFallbackPath: true,
        tooltip: "Access and update patient chart notes and medical records"
      },
      ...(isMedicalScribeEnabled && isScribeInsightsEnabled
        ? [
            {
              to: "scribe-insights",
              primary: "Scribe Insights",
              Icon: <AutoAwesomeOutlinedIcon />,
              isFallbackPath: false,
              tooltip:
                "AI-extracted insights from this patient's scribe visits",
              badgeContent: scribeInsightsCount
            }
          ]
        : []),
      ...(isMedicalScribeEnabled
        ? [
            {
              to: "medical-scribes",
              primary: "Scribes",
              Icon: <AudioFileIcon />,
              isFallbackPath: false,
              tooltip: "Ambient scribe recordings for this patient"
            }
          ]
        : []),
      ...(isFollowUpsEnabled
        ? [
            {
              to: "action-items",
              primary: "Action Items",
              Icon: <AutoAwesomeIcon />,
              isFallbackPath: false,
              tooltip:
                "Action items the model found in this patient's scribe visits"
            }
          ]
        : []),

      {
        to: "hearing-aids",
        primary: "Hearing Aids",
        Icon: <EarIcon />,
        isFallbackPath: false,
        subItems: [
          {
            to: "orders",
            primary: "Orders",
            Icon: <Inventory2Icon />,
            tooltip: "Hearing Aid and Accessory Orders"
          },
          {
            to: "forms",
            primary: "Forms",
            Icon: <ListAltIcon />,
            tooltip: "Hearing Aid Forms"
          },
          ...(flags[HEARING_AID_BENEFIT_CALCULATOR]
            ? [
                {
                  to: "benefit-calculator",
                  primary: "Benefit Calculator",
                  Icon: <CalculateOutlinedIcon />,
                  tooltip:
                    "Estimate the patient's hearing aid out-of-pocket cost"
                }
              ]
            : [])
        ]
      },
      {
        to: "tasks",
        primary: "Tasks",
        Icon: <ChecklistIcon />,
        isFallbackPath: false,
        badgeContent: patientOpenTasksCount
      },
      {
        to: "alerts",
        primary: "Alerts",
        Icon: <AnnouncementIcon />,
        isFallbackPath: false,
        tooltip: "View and manage patient alerts"
      },
      ...(hasNoah
        ? [
            {
              to: "noah",
              primary: "Noah Sessions",
              Icon: <CloudCircleIcon />,
              isFallbackPath: false,
              tooltip: "Noah sessions"
            }
          ]
        : []),
      {
        to: "audiology",
        primary: "Reports",
        Icon: <AudiologyIcon />,
        isFallbackPath: false,
        featureFlag: AUDIOLOGY_REPORTS_FEATURE,
        tooltip: "Generate and review detailed audiology reports",
        subItems: [
          {
            to: "audiograms",
            primary: "Audiograms",
            Icon: <ShowChartOutlinedIcon />,
            tooltip: "View multiple audiograms side by side",
            annotation: audiogramCompareChip
          },
          // Practice attribute, not an LD flag — the same switch that gates the REM report section.
          ...(isRealEarMeasurementEnabled
            ? [
                {
                  to: "real-ear-measurements",
                  primary: "Real Ear Measurements",
                  Icon: <HearingIcon />,
                  tooltip:
                    "Review REM sessions with their appointment and hearing aids"
                },
                {
                  to: "test-box",
                  primary: "Test Box",
                  Icon: <ScienceOutlinedIcon />,
                  tooltip:
                    "Review hearing aid test box (EAE) checks with their appointment and hearing aids"
                }
              ]
            : [])
        ]
      },
      {
        to: "billing",
        primary: "Patient Billing",
        Icon: <BillingIcon />,
        isFallbackPath: false,
        tooltip: "Manage patient invoices and payment records"
      },
      {
        to: "appointments",
        primary: "Appointments",
        Icon: <AppointmentsIcon />,
        isFallbackPath: false,
        tooltip: "View and manage patient appointments",
        ...(activeVisitAppointmentId
          ? {
              isOpenedByDefault: true,
              subItems: [
                {
                  to: String(activeVisitAppointmentId),
                  primary: "Active Visit",
                  Icon: <FiberManualRecordIcon />,
                  tooltip: "Open the patient's current active visit"
                }
              ]
            }
          : {})
      },
      {
        to: "insurance-policies",
        primary: "Insurance Policies",
        Icon: <InsurancePolicyIcon />,
        isFallbackPath: false,
        tooltip: "Manage and review patient insurance policies"
      },
      {
        to: "profile",
        primary: "Patient Profile",
        Icon: <ProfileIcon />,
        isFallbackPath: false,
        tooltip: "Patient’s personal details"
      },
      ...(patientPortalEnabled
        ? [
            {
              to: "patient-portal",
              primary: "Patient Portal",
              Icon: <FolderSharedIcon />,
              isFallbackPath: false,
              tooltip:
                "Documents and videos shared with the patient through the portal"
            }
          ]
        : []),
      {
        to: "onboardings",
        primary: "Onboarding Forms",
        Icon: <ChecklistRtl />,
        isFallbackPath: false,
        featureFlag: PATIENT_ONBOARDING_FORMS_FEATURE_FLAG,
        tooltip: "Patient’s onboarding forms"
      },
      {
        to: "documents",
        primary: "Docs & Letters",
        Icon: <FeedIcon />,
        isFallbackPath: false,
        featureFlag: PATIENT_DOCUMENTS_FEATURE,
        tooltip: "Create, manage, and send patient forms and letters"
      },
      {
        to: "e-documents",
        primary: "E-Documents",
        Icon: <CloudUploadIcon />,
        isFallbackPath: false,
        tooltip: "Upload, manage, and access electronic documents",
        annotation: eDocumentsAnnotation
      },
      {
        to: "visit-tracker",
        primary: "Visit Tracker",
        Icon: <PersonSearchIcon />,
        isFallbackPath: false,
        tooltip: "Create, manage, and access Trackers",
        featureFlag: VISIT_TRACKER_FEATURE
      },
      {
        to: "touchpoints",
        primary: "Touchpoints",
        featureFlag: MARKETING_AUTOMATION_FEATURE,
        Icon: <ConnectWithoutContactIcon />,
        isFallbackPath: false,
        isOpenedByDefault: true,
        tooltip: "Track and manage patient interactions and follow-ups",
        subItems: [
          {
            to: "messages",
            primary: "Messages",
            Icon: <MessagesIcon />,
            tooltip: "Messages"
          },
          {
            to: "emails",
            primary: "Emails",
            Icon: <EmailIcon />,
            tooltip: "Emails"
          },
          {
            to: "faxes",
            primary: "Faxes",
            Icon: <FaxIcon />,
            tooltip: "Faxes"
          },
          ...(canUseVoiceScreens
            ? [
                {
                  to: "calls",
                  primary: "Calls",
                  Icon: <CallIcon />,
                  // Same gate as the practice-level /calls page: Voice
                  // access, not a role (ENG-2315).
                  tooltip: "Call history for this patient"
                }
              ]
            : []),
          ...(isDirectMailEnabled
            ? [
                {
                  to: "direct-mail",
                  primary: "Direct Mail",
                  Icon: <MarkunreadMailboxIcon />,
                  tooltip: "Mailers Darwill sent this patient"
                }
              ]
            : [])
        ]
      },
      ...(quickbooksIsAvailable
        ? [
            {
              to: "quickbooks",
              primary: "QuickBooks",
              Icon: (
                <SvgIcon>
                  <QBSyncIcon />
                </SvgIcon>
              ),
              isFallbackPath: false,
              tooltip: "QuickBooks access"
            }
          ]
        : []),
      ...(isPodiumIntegrationEnabled && isAdminOrBusinessOwner
        ? [
            {
              to: "podium",
              primary: "Podium",
              Icon: (
                <SvgIcon>
                  <PodiumIcon />
                </SvgIcon>
              ),
              isFallbackPath: false,
              tooltip: "Podium sync details"
            }
          ]
        : []),
      ...(isGoogleReviewsEnabled
        ? [
            {
              to: "google-reviews",
              primary: "Google Reviews",
              Icon: <StarIcon />,
              isFallbackPath: false,
              tooltip: "Google Reviews matched to this patient"
            }
          ]
        : []),
      ...(canUseVoiceScreens && !isTouchpointsVisible
        ? [
            {
              to: "calls",
              primary: "Calls",
              Icon: <CallIcon />,
              isFallbackPath: false,
              // Same gate as the practice-level /calls page.
              tooltip: "Call history for this patient"
            }
          ]
        : [])
    ];

    const scopedMenuItems = isScheduler
      ? allMenuItems.filter((item) => item.to !== "billing")
      : allMenuItems;

    return scopedMenuItems.filter(
      ({ roles, permissions, featureFlag }) =>
        (!featureFlag || flags[featureFlag]) &&
        (!hasAccessRestrictions(roles, permissions) ||
          hasRequiredAccess(
            currentUser,
            userRoles,
            userPermissions,
            roles,
            permissions
          ))
    );
  }, [
    flags,
    hasNoah,
    quickbooksIsAvailable,
    patientPortalEnabled,
    patientOpenTasksCount,
    scribeInsightsCount,
    isAiSummarizeEnabled,
    isMedicalScribeEnabled,
    isScribeInsightsEnabled,
    isFollowUpsEnabled,
    isPodiumIntegrationEnabled,
    isGoogleReviewsEnabled,
    canUseVoiceScreens,
    isRealEarMeasurementEnabled,
    isTouchpointsVisible,
    isDirectMailEnabled,
    isAdminOrBusinessOwner,
    isScheduler,
    audiogramCompareChip,
    eDocumentsAnnotation,
    activeVisitAppointmentId,
    currentUser,
    userRoles,
    userPermissions
  ]);

  return menuItems;
}
